import { NetworkStatus, gql, useQuery } from "@apollo/client";
import {
  DispatcherOrderEntryAdditionalField,
  PackageSize,
  ShipmentStatus,
  ShipmentStopType,
  ShipmentType,
} from "@api/graphql/generated/generated-types";

export const EDIT_ORDER_SHIPMENT_QUERY = gql`
  query EditOrderShipment($courierId: ID!, $shipmentId: ID!) {
    shipmentById(courierId: $courierId, shipmentId: $shipmentId) {
      id
      shipmentDate
      status
      packageSize
      numPackages
      type
      packageWeightInPounds
      packageDescription
      packageType {
        id
      }
      vehicleType {
        id
      }
      packageItems {
        width
        height
        length
        weight
        packageTypeId
        quantity
      }
      driver {
        id
        firstName
        photoUrl
        avatarDisplay
      }
      stops {
        id
        type
        stopDate
        effectiveStopDate
        stopSequenceNumber
        orderEntrySequenceNumber
        stopConsolidatedSortingNumber
        completedAt
        streetAddress
        city
        state
        country
        zipCode
        companyName
        contactName
        googlePlaceId
        instruction
        phone
        suite
        locationId
        additionalDriverQuestionListId
        timeWindow {
          open
          close
        }
        tasks {
          sequenceNumber
        }
      }
      order {
        id
        displayId
        courierId
        caller
        callerPhone
        endCustomerReferenceNumber
        additionalEmailRecipients
        costCenter {
          id
          name
        }
        serviceType {
          id
          name
        }
        endCustomer {
          id
          displayId
          companyName
          firstName
          lastName
          contactEmail
          contactPhoneNumber
          suite
          dispatcherOrderEntryAdditionalFields
        }
        customFieldValues {
          valueText
          valueInteger
          valueNumber
          field {
            id
          }
        }
      }
    }
  }
`;

export interface EditOrderShipmentStop {
  id: string;
  type: ShipmentStopType;
  stopDate?: string | null;
  effectiveStopDate?: string | null;
  stopSequenceNumber?: number | null;
  orderEntrySequenceNumber?: number | null;
  stopConsolidatedSortingNumber?: number | null;
  completedAt?: number | null;
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zipCode?: string | null;
  companyName?: string | null;
  contactName?: string | null;
  googlePlaceId?: string | null;
  instruction?: string | null;
  phone?: string | null;
  suite?: string | null;
  locationId?: string | null;
  additionalDriverQuestionListId?: string | null;
  timeWindow?: {
    open?: string | null;
    close?: string | null;
  } | null;
  tasks?: Array<{
    sequenceNumber: number;
  }> | null;
}

export interface EditOrderShipment {
  id: string;
  shipmentDate: string;
  status: ShipmentStatus;
  packageSize?: PackageSize | null;
  numPackages?: number | null;
  type: ShipmentType;
  packageWeightInPounds?: number | null;
  packageDescription?: string | null;
  packageType?: { id: string } | null;
  vehicleType?: { id: string } | null;
  packageItems?: Array<{
    width?: number | null;
    height?: number | null;
    length?: number | null;
    weight?: number | null;
    packageTypeId?: string | null;
  }> | null;
  driver?: {
    id: string;
    firstName?: string | null;
    photoUrl?: string | null;
    avatarDisplay?: string | null;
  } | null;
  stops: EditOrderShipmentStop[];
  order: {
    id: string;
    displayId: number;
    courierId: string;
    caller?: string | null;
    callerPhone?: string | null;
    endCustomerReferenceNumber?: string | null;
    additionalEmailRecipients?: string[] | null;
    costCenter?: {
      id: string;
      name: string;
    } | null;
    serviceType?: {
      id: string;
      name: string;
    } | null;
    endCustomer: {
      id: string;
      displayId: number;
      companyName?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      contactEmail: string;
      contactPhoneNumber: string;
      suite?: string | null;
      dispatcherOrderEntryAdditionalFields?: DispatcherOrderEntryAdditionalField[] | null;
    };
    customFieldValues: Array<{
      valueText?: string | null;
      valueInteger?: number | null;
      valueNumber?: number | null;
      field: {
        id: string;
      };
    }>;
  };
}

interface EditOrderShipmentQueryData {
  shipmentById: EditOrderShipment | null;
}

interface EditOrderShipmentQueryVariables {
  courierId: string;
  shipmentId: string;
}

interface UseEditOrderShipmentOptions {
  shipmentId: string;
  courierId: string | undefined;
  skip?: boolean;
}

export function useEditOrderShipment({
  shipmentId,
  courierId,
  skip = false,
}: UseEditOrderShipmentOptions) {
  const { data, loading, error, refetch, networkStatus } = useQuery<
    EditOrderShipmentQueryData,
    EditOrderShipmentQueryVariables
  >(EDIT_ORDER_SHIPMENT_QUERY, {
    variables: { courierId: courierId!, shipmentId },
    skip: skip || !courierId,
    fetchPolicy: "cache-and-network",
  });

  return {
    shipment: data?.shipmentById ?? undefined,
    loading,
    // `loading` is also true while a refetch is in flight, which must not be
    // treated as the first load or the modal would be torn down mid-save.
    isInitialLoading: networkStatus === NetworkStatus.loading,
    error,
    refetch,
  };
}
